import React from 'react';
import styled, { css } from 'styled-components';

import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';

// Same look as TabsNavigation title bubble
const Bubble = styled.span<{ $clickable: boolean }>`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  color: var(--white);
  font-family: var(--standardFontFamily);
  font-size: var(--small);
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;

  border-radius: 2px;
  background: var(--gray2a);
  padding: 1px 4px;
  margin-left: 4px;

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover {
        filter: brightness(1.2);
      }
    `}
`;

const ExpandIcon = styled(MaterialSymbol)`
  font-size: 14px;
  margin-right: -2px;
`;

type Props = {
  /** Number of fills in the aggregation group of the row (see aggregateFillsByOrder) */
  aggregatedFillsCount?: number;
  /** Row is an individual fill of an expanded aggregation group */
  aggregationExpanded?: boolean;
  /** Position of the row within the expanded aggregation group */
  aggregatedFillIndex?: number;
  /** Expand / collapse the aggregation group of the row (not available = display only) */
  onToggle?: () => void;
};

/** Lozenge with fills count of an aggregated row (or fill position when expanded) */
export default function FillsCountBubble({
  aggregatedFillsCount = 1,
  aggregationExpanded = false,
  aggregatedFillIndex,
  onToggle,
}: Props) {
  if (aggregatedFillsCount <= 1) {
    return null;
  }
  return (
    <Bubble
      $clickable={!!onToggle}
      title={
        onToggle
          ? aggregationExpanded
            ? 'Collapse fills'
            : 'Show individual fills'
          : undefined
      }
      onClick={
        onToggle
          ? event => {
              event.stopPropagation();
              onToggle();
            }
          : undefined
      }
    >
      {aggregationExpanded && aggregatedFillIndex
        ? `Fill ${aggregatedFillIndex}`
        : `${aggregatedFillsCount} fills`}
      {onToggle &&
        // Collapse icon only on the top row of the expanded group
        // (rows are numbered from the bottom)
        (!aggregationExpanded ||
          aggregatedFillIndex === aggregatedFillsCount) && (
          <ExpandIcon
            icon={aggregationExpanded ? 'expand_less' : 'expand_more'}
            fontSize="inherit"
          />
        )}
    </Bubble>
  );
}
